import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Gauge } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import { StatusPill, Stat, StatGroup, EmptyState, type StatusTone } from '@vnetwork/ui/data';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart } from '@vnetwork/ui/charts';
import {
  useKumaStreamDetail,
  useKumaStreamChart,
  type KumaStreamDetail,
  type KumaChartRange,
} from '@/api/monitoring/prometheus';

const CHART_RANGES: KumaChartRange[] = ['recent', '3h', '6h', '24h', '1w'];

const fmtMs = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)} ms`);
const fmtMs1 = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)} ms`);
const fmtPct = (v: number | null | undefined) =>
  v == null ? '—' : `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
const fmtDays = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)} days`);

function statusOf(status: number | null): { tone: StatusTone; label: string } {
  if (status === 1) return { tone: 'success', label: 'Up' };
  if (status === 0) return { tone: 'danger', label: 'Down' };
  return { tone: 'muted', label: 'Unknown' };
}

/**
 * Uptime-Kuma-style per-monitor detail: status, current/avg response, uptime
 * windows, cert expiry, and an "Avg Ping" latency chart. Everything comes from
 * one packaged call (`useKumaStreamDetail`), keyed by the monitor's name.
 */
export function KumaMonitorDetail() {
  const { projectId, monitorName } = useParams<{ projectId: string; monitorName: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const listPath = `${base}/kuma-uptime`;
  // react-router v6 already URL-decodes path params.
  const name = monitorName ?? '';

  const { data, isLoading, isError, refetch } = useKumaStreamDetail(name);

  const eyebrow = (
    <Breadcrumbs>
      <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
      <BreadcrumbItem asChild><Link to={listPath}>Uptime</Link></BreadcrumbItem>
      <BreadcrumbItem current>{name}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <PageHeader icon={<Gauge size={22} />} eyebrow={eyebrow} title={name} />
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <StatGroup columns={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 56 }} />
            ))}
          </StatGroup>
          <Skeleton style={{ height: 300 }} />
        </div>
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container size="xl">
        <PageHeader icon={<Gauge size={22} />} eyebrow={eyebrow} title={name} />
        <Alert
          tone="danger"
          title="Couldn't load monitor"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable. Please try again.
        </Alert>
      </Container>
    );
  }

  return <MonitorDetailBody d={data} name={name} eyebrow={eyebrow} />;
}

function MonitorDetailBody({
  d,
  name,
  eyebrow,
}: {
  d: KumaStreamDetail;
  name: string;
  eyebrow: React.ReactNode;
}) {
  const status = statusOf(d.status);
  const [range, setRange] = useState<KumaChartRange>('24h');
  const { data: chart } = useKumaStreamChart(name, range);

  // Min/Avg/Max bands aligned by index (same range params → same timestamps).
  const rows = useMemo(() => {
    const avg = chart?.avg ?? [];
    const min = chart?.min ?? [];
    const max = chart?.max ?? [];
    return avg.map((p, i) => ({
      label: new Date(p.t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      min: min[i] ? Math.round(min[i].v) : null,
      avg: Math.round(p.v),
      max: max[i] ? Math.round(max[i].v) : null,
    }));
  }, [chart]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Gauge size={22} />}
        eyebrow={eyebrow}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {name}
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </span>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          <StatGroup columns={3}>
            <Stat label="Response (Current)" value={fmtMs(d.response_ms)} />
            <Stat label="Avg. Response (24h)" value={fmtMs1(d.avg_response_ms_24h)} />
            <Stat label="Cert Exp." value={fmtDays(d.cert_days_remaining)} />
            <Stat label="Uptime (24h)" value={fmtPct(d.uptime_24h)} />
            <Stat label="Uptime (30d)" value={fmtPct(d.uptime_30d)} />
            <Stat label="Uptime (1y)" value={fmtPct(d.uptime_1y)} />
          </StatGroup>
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <CardTitle>Response Time (Min / Avg / Max)</CardTitle>
            <div style={{ width: 120 }}>
              <Select value={range} onValueChange={(v) => setRange(v as KumaChartRange)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHART_RANGES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r === 'recent' ? 'Recent' : r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {rows.length ? (
            <Chart
              type="area"
              data={rows}
              xKey="label"
              series={[
                { key: 'max', label: 'Max Ping' },
                { key: 'avg', label: 'Avg Ping' },
                { key: 'min', label: 'Min Ping' },
              ]}
              height={300}
              valueFormatter={(v) => `${Number(v)} ms`}
            />
          ) : (
            <EmptyState icon={<Gauge size={28} />} title="No response data" size="sm" />
          )}
        </CardBody>
      </Card>
      </div>
    </Container>
  );
}
